import React, { FC, useState } from 'react'

import styled from 'styled-components'

import { Modal, Txt, StandardButton, Icon } from 'src/components/atoms'
import { FormItemTitle } from 'src/components/molecules'

import { useAuth, useToast, useDevice } from 'src/hooks'
import { UserEntity, PasswordForceUpdateFormEntity } from 'src/types'

const ContentTextAreaContainer = styled.div`
  width: 11.94rem;
  height: 1.63rem;
  font-size: 1.2rem;
`

const TitleWrapper = styled.div<{ width: number | undefined; isPC: boolean }>`
  // PCサイズ以下の場合タイトル部分がFormItemの上にくるため、widthはauto固定
  width: ${props => (props.isPC ? (props.width ? `${props.width}rem` : '7.44rem') : 'auto')};
  flex-shrink: 0;
  margin-right: var(--s-1);
`

type Props = {
  isOpen: boolean
  user: UserEntity
  password: string
  onClose: () => void
  afterUpdate?: () => void
}

export const PasswordCopyEmailModal: FC<Props> = ({
  isOpen,
  user,
  password,
  onClose,
  afterUpdate,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { sendForceUpdateUserPasswordEmail, sendForceUpdateExternalUserPasswordEmail } = useAuth()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const [showPassword, setShowPassword] = useState(false)

  const passwordText = user.isExternal ? 'パスワード' : '仮パスワード'

  const onSubmit = async () => {
    setIsLoading(true)

    const formEntity: PasswordForceUpdateFormEntity = {
      password,
      isSendEmail: true,
    }

    try {
      await (user.isExternal
        ? sendForceUpdateExternalUserPasswordEmail({ userId: user.id, formEntity })
        : sendForceUpdateUserPasswordEmail({ userId: user.id, formEntity }))
      displayStandardTextToast(`${passwordText}発行メールを送信しました`)

      afterUpdate && afterUpdate()
    } catch {
      displayErrorTextToast(`${passwordText}発行メールの送信に失敗しました`)
    } finally {
      onClose()
      setIsLoading(false)
      setShowPassword(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      displayStandardTextToast('コピーしました')
    } catch {
      displayErrorTextToast('コピーに失敗しました')
    }
  }

  const { isPC } = useDevice()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="md"
      headerInner={
        <div className="tw-px-s1">
          <div className="tw-flex tw-items-center">
            <Icon
              size="--r1"
              path="refresh-ccw/soft/line"
              className="tw-accent-standardText-secondary"
            />
            <Txt sizeType="l" className="tw-ml-r-3 tw-font-bold">
              パスワードをリセットしました
            </Txt>
          </div>
        </div>
      }
      canCloseClickOuter={false}
    >
      <div className="tw-w-[37.86rem] tw-p-s2">
        <div className="tw-centering">
          <TitleWrapper width={5.72} isPC={isPC}>
            <FormItemTitle
              text={<span className="tw-text-standardText-secondary">{passwordText}</span>}
            />
          </TitleWrapper>
          <div className="tw-mt-6">
            <button type="button" onClick={() => copyToClipboard(password)}>
              <ContentTextAreaContainer className="tw-flex tw-justify-start tw-items-center tw-w-full tw-border-b tw-border-b-border-secondary">
                <span className="tw-h-[1.25rem]">
                  {showPassword ? password : '*'.repeat(password.length)}
                </span>
              </ContentTextAreaContainer>
            </button>
            <Icon
              path={`${showPassword ? 'eye' : 'eye-off'}/soft/line`}
              size="--r1"
              className="tw-text-standardText-secondary hover:tw-text-standardText-primary tw-ml-s-1 tw-cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            />
          </div>
        </div>

        <div className="tw-centering tw-mt-s3">
          <StandardButton
            buttonType="secondary"
            text={`${passwordText}発行メールを送信`}
            prependIconPath="send/soft/line"
            size="s"
            isLoading={isLoading}
            onClick={onSubmit}
          />
        </div>
        <div className="tw-centering tw-mt-s3">
          <StandardButton
            type="button"
            buttonType="secondary"
            text="閉じる"
            prependIconPath="close/soft/line"
            size="l"
            onClick={onClose}
          />
        </div>
      </div>
    </Modal>
  )
}
